import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Calendar, ExternalLink, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const BookDemoModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", firm: "", email: "", phone: "", message: "" });
  const [booked, setBooked] = useState(false);
  const [calendly, setCalendly] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/demo", form);
      setCalendly(data.calendly_url);
      setBooked(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setBooked(false);
    setForm({ name: "", firm: "", email: "", phone: "", message: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        {!booked ? (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50">
                <Calendar className="h-6 w-6 text-indigo-700" />
              </div>
              <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Book a Demo</DialogTitle>
              <DialogDescription className="text-slate-500">Tell us a bit about you and we'll take you to schedule a slot.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input required value={form.name} onChange={set("name")} placeholder="Jane Doe" className="rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Firm</Label><Input value={form.firm} onChange={set("firm")} placeholder="Firm name" className="rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} placeholder="+91..." className="rounded-xl" /></div>
              </div>
              <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.email} onChange={set("email")} placeholder="you@firm.co" className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Message</Label><Textarea value={form.message} onChange={set("message")} placeholder="What would you like to see?" className="rounded-xl" rows={3} /></div>
              <Button type="submit" disabled={submitting} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
                {submitting ? "Submitting..." : "Continue to scheduling"}
              </Button>
            </form>
          </>
        ) : (
          <div className="py-4 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1a1a2e]">You're all set!</h3>
            <p className="mt-2 text-sm text-slate-500">Pick a time that works for you on our calendar.</p>
            <a href={calendly} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#2f2b8f] px-6 py-3 text-sm font-semibold text-white hover:bg-[#26236f]">
              Open scheduling page <ExternalLink className="h-4 w-4" />
            </a>
            <p className="mt-3 text-xs text-slate-400">(Calendly placeholder — replace with your real link)</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookDemoModal;
